import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Star,
  Sparkles,
  Globe2,
  Compass,
  Zap,
  LineChart,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { fetchCaseStudies } from "@/lib/api";
import BrandMarquee from "@/components/BrandMarquee";
import { TIDS } from "@/constants/testIds";

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.2, 0.8, 0.2, 1], delay: i * 0.08 },
  }),
};

export default function Home() {
  const [cases, setCases] = useState([]);
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.6]);

  useEffect(() => {
    fetchCaseStudies()
      .then(setCases)
      .catch(() => {});
  }, []);

  // Cursor-follow glow on hero
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      el.style.setProperty("--mx", `${e.clientX - rect.left}px`);
      el.style.setProperty("--my", `${e.clientY - rect.top}px`);
    };
    el.addEventListener("mousemove", onMove);
    return () => el.removeEventListener("mousemove", onMove);
  }, []);

  const featured = cases.slice(0, 4);

  return (
    <div className="relative">
      {/* ============================ HERO ============================ */}
      <section
        ref={heroRef}
        className="relative min-h-[100svh] pt-32 pb-16 overflow-hidden grain spotlight cursor-glow"
      >
        <motion.div
          style={{ y: heroY, opacity: heroOpacity }}
          className="max-w-[1240px] mx-auto px-6 sm:px-10 relative z-10"
        >
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="flex flex-wrap items-center gap-3"
          >
            <span className="tag-pill copper">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF3D6A] inline-block" />
              Open for Q1 2026 briefs
            </span>
            <span className="text-xs sm:text-sm uppercase tracking-[0.35em] text-[#0B0B0B]/70 font-semibold">
              Est. 2016 <span className="rule-v" /> Kolkata → Worldwide
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="display-mega mt-8"
          >
            We make brands
            <br />
            <span className="copper">impossible</span> to
            <br />
            ignore.
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            className="mt-8 font-display text-2xl sm:text-3xl lg:text-4xl text-[#1D2AE8] leading-tight max-w-3xl"
          >
            Your{" "}
            <em className="font-italic-serif text-[#FF3D6A]">Premium</em>{" "}
            digital marketing{" "}
            <span className="inline-flex items-center align-baseline">
              <span className="mx-1 inline-flex items-center justify-center w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-[#0B0B0B] text-[#FFC629] font-display text-lg sm:text-2xl leading-none">
                ID
              </span>
            </span>
            .
          </motion.p>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={4}
            className="mt-10 max-w-2xl text-lg sm:text-xl text-[#1D2AE8]/85 leading-relaxed"
          >
            An independent digital studio for the ambitious few. From
            avant-garde streetwear in Mumbai to century-old trouser makers in
            Yorkshire, from super-speciality hospitals in Gurgaon to Berlin
            workshops shaping global taste — we build the language, the numbers
            and the aura for brands worth remembering.
          </motion.p>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={5}
            className="mt-4 max-w-2xl text-base text-[#0B0B0B]/70 leading-relaxed font-italic-serif"
          >
            One signed promise, kept for one hundred and ten brands: we double
            your digital growth within twelve months, or we work at our cost
            until we do.
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={6}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact"
              data-testid={TIDS.hero.ctaPrimary}
              className="btn-ink"
            >
              Start a project <ArrowUpRight size={18} />
            </Link>
            <Link
              to="/work"
              data-testid={TIDS.hero.ctaSecondary}
              className="btn-outline-ink"
            >
              See the work
            </Link>
            <span className="hidden sm:inline-flex items-center gap-2 text-sm text-[#0B0B0B]/60">
              <Star size={14} fill="#FF3D6A" strokeWidth={0} />
              4.98 / 5 · founder-rated
            </span>
          </motion.div>

          {/* Floating stats */}
          <div className="mt-24 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              { k: "Top 15", v: "Agencies in India", label: "Rank" },
              { k: "110", v: "38 Indian · 72 Global", label: "Clients" },
              { k: "12", v: "Strategists · artists · engineers", label: "Team" },
            ].map((s, i) => (
              <motion.div
                key={s.label}
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={5 + i}
                className={`glass-dark rounded-3xl p-6 floaty ${
                  i === 1 ? "delay-1" : i === 2 ? "delay-2" : ""
                }`}
              >
                <p className="text-xs uppercase tracking-[0.25em] text-[#FFC629]">
                  {s.label}
                </p>
                <p className="font-display text-4xl mt-2">{s.k}</p>
                <p className="text-[#FFF6E0]/70 text-sm mt-1">{s.v}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ============================ MARQUEE ============================ */}
      <BrandMarquee />

      {/* ============================ CAPABILITIES ============================ */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-[1240px] mx-auto px-6 sm:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#1D2AE8]/70 font-semibold">
                What we do
              </p>
              <h2 className="font-display text-5xl sm:text-6xl mt-3 leading-[0.95]">
                Four disciplines,
                <br />
                <em className="font-italic-serif">one obsession.</em>
              </h2>
            </div>
            <p className="max-w-md text-[#1D2AE8]/80 leading-relaxed">
              We're not a full-service agency. We're a specialist studio built
              around four things brands actually pay us to change.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: <Compass size={22} />,
                t: "Brand Direction",
                d: "Positioning, naming, verbal identity — the language your brand will still be quoting five years from now.",
              },
              {
                icon: <Sparkles size={22} />,
                t: "Editorial Creative",
                d: "Campaigns shot and written to magazine-cover standard. Content people save, not scroll.",
              },
              {
                icon: <LineChart size={22} />,
                t: "Performance",
                d: "Media planning, paid social, SEO and CRO — orchestrated against one honest growth number.",
              },
              {
                icon: <Zap size={22} />,
                t: "Community & PR",
                d: "Influencer, community, editorial press — the earned side of growth, done with taste.",
              },
            ].map((c, i) => (
              <motion.div
                key={c.t}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className="glass-light rounded-3xl p-7 h-full"
              >
                <div className="text-[#FF3D6A]">{c.icon}</div>
                <p className="font-display text-2xl mt-6">{c.t}</p>
                <p className="text-sm text-[#1D2AE8]/75 mt-3 leading-relaxed">
                  {c.d}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ PROMISE STRIP ============================ */}
      <section className="relative py-24 sm:py-28 bg-[#FFE0EC]">
        <div className="max-w-[1240px] mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="text-xs uppercase tracking-[0.3em] text-[#1D2AE8]/70 font-semibold">
              The Illustrative Promise
            </p>
            <h2 className="font-display text-5xl sm:text-6xl leading-[0.95] mt-4">
              Double your{" "}
              <em className="font-italic-serif text-[#FF3D6A]">
                digital growth
              </em>{" "}
              in one year.
            </h2>
            <p className="mt-6 text-[#1D2AE8]/80 text-lg leading-relaxed max-w-xl">
              Not a marketing line — a written commitment. Every engagement
              starts with a signed north-star metric and a twelve-month curve
              we bet the retainer on.
            </p>
            <p className="mt-4 text-[#1D2AE8]/75 leading-relaxed max-w-xl">
              We've kept it for one hundred and ten brands. If we ever miss,
              we work at our cost until the number hits. That's rare in this
              industry — and it's the whole point of ours.
            </p>
            <Link to="/about" className="btn-ink mt-8 inline-flex">
              Read the promise <ArrowUpRight size={18} />
            </Link>
          </div>

          <div className="lg:col-span-6 grid grid-cols-2 gap-4">
            <div className="glass-light rounded-3xl p-6">
              <p className="tag-pill">Signature</p>
              <p className="font-display text-2xl mt-4">Editorial-grade</p>
              <p className="text-sm text-[#1D2AE8]/70 mt-1">
                Every asset held to magazine-cover standard.
              </p>
            </div>
            <div className="glass-light rounded-3xl p-6">
              <p className="tag-pill">Global</p>
              <p className="font-display text-2xl mt-4">Four continents</p>
              <p className="text-sm text-[#1D2AE8]/70 mt-1">
                Native execution across India, UK, EU & US.
              </p>
            </div>
            <div className="glass-light rounded-3xl p-6 col-span-2">
              <p className="tag-pill copper">Measured</p>
              <p className="font-display text-2xl mt-4">
                One number. Signed on paper.
              </p>
              <p className="text-sm text-[#1D2AE8]/70 mt-1">
                Revenue, qualified leads, subscribers or sell-through — we pick
                one, sign it, and ship against it every quarter.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ FEATURED WORK ============================ */}
      <section className="relative py-28">
        <div className="max-w-[1240px] mx-auto px-6 sm:px-10">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#1D2AE8]/70 font-semibold">
                Selected Work
              </p>
              <h2 className="font-display text-5xl sm:text-6xl mt-3 leading-[0.95]">
                Four favourites,
                <br />
                <em className="font-italic-serif">many more inside.</em>
              </h2>
            </div>
            <Link to="/work" className="btn-outline-ink self-start">
              View all case studies <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
            {featured.map((c, idx) => (
              <motion.div
                key={c.slug}
                data-testid={TIDS.card(c.slug)}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: idx * 0.08 }}
                className={`case-card glass-light rounded-3xl overflow-hidden ${
                  idx === 0 ? "md:col-span-4" : "md:col-span-2"
                }`}
              >
                <div className="img-wrap aspect-[4/3] rounded-none">
                  <img src={c.image} alt={c.name} loading="lazy" />
                </div>
                <div className="p-6 flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.25em] text-[#1D2AE8]/60">
                      {c.category}
                    </p>
                    <p className="font-display text-3xl mt-2">{c.name}</p>
                  </div>
                  <span className="bg-[#0B0B0B] text-[#FFC629] rounded-full px-3 py-1 text-xs font-semibold whitespace-nowrap">
                    {c.metric}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ PULL QUOTE ============================ */}
      <section className="relative py-28 bg-[#1D2AE8] text-[#FFF6E0] overflow-hidden">
        <div className="absolute inset-0 grain opacity-70 pointer-events-none" />
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 relative z-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#FFC629] font-semibold">
            Founders on Illustrative
          </p>
          <blockquote className="font-display text-4xl sm:text-6xl leading-[1.02] mt-8">
            "The rigour we expect from Berlin creatives,
            <em className="font-italic-serif text-[#FFC629]">
              {" "}
              delivered from Kolkata.
            </em>{" "}
            They shaped a global campaign narrative that felt native to every
            market."
          </blockquote>
          <div className="mt-10 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#FFC629] text-[#1D2AE8] font-display flex items-center justify-center">
              MV
            </div>
            <div>
              <p className="font-semibold">Maximilian Voss</p>
              <p className="text-sm text-[#FFF6E0]/60">
                Brand Director · 032c Workshop, Berlin
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ CTA ============================ */}
      <section className="relative py-28">
        <div className="max-w-[1000px] mx-auto px-6 sm:px-10 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#1D2AE8]/70 font-semibold">
            Chapter 111
          </p>
          <h2 className="font-display text-5xl sm:text-7xl leading-[0.95] mt-4">
            Ready to become
            <br />
            the next <em className="font-italic-serif">illustrative</em> case
            study?
          </h2>
          <p className="mt-6 text-[#1D2AE8]/80 text-lg max-w-xl mx-auto">
            We take on a handful of brands each quarter — chosen for taste,
            ambition and appetite. Send us a brief; we'll reply in person
            within twenty-four hours.
          </p>
          <Link to="/contact" className="btn-ink mt-10 inline-flex">
            Book an intro call <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
